import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  CalendarDays,
  Check,
  Clipboard,
  CreditCard,
  ExternalLink,
  Landmark,
  Link2,
  Megaphone,
  Pencil,
  ShoppingBag,
  UserRound,
  Users,
  Wallet,
} from "lucide-react";
import { toastEmmit } from "../../../Helper/Toastr";
import { APIBaseURL } from "../../../Environment/Environment";
import { GetService } from "../../../Services/ConstantService";
import { API_URL } from "../../../Services/ApiService";

const formatMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(value) || 0);

const display = (value) => {
  if (value === null || value === undefined) return "Not provided";

  if (typeof value === "string") {
    return value.trim() || "Not provided";
  }

  return String(value);
};

const maskValue = (value) => {
  if (!value) return "Not provided";

  return `•••• ${String(value).slice(-4)}`;
};

const getImageSource = (path) => {
  if (!path) return "assets/img/breadcrumb/dummy-1.png";

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const apiOrigin = APIBaseURL;

  if (!apiOrigin) {
    return `/${String(path).replace(/^\/+/, "")}`;
  }

  return new URL(
    String(path).replace(/^\/+/, ""),
    `${apiOrigin.replace(/\/$/, "")}/`,
  ).toString();
};

const statusText = (status) => {
  if (typeof status !== "string") {
    return "Pending";
  }

  const value = status.toLowerCase().replace(/[_-]/g, " ").trim();

  if (value === "under review" || value === "review") {
    return "Under Review";
  }

  return value
    ? value.replace(/\b\w/g, (letter) => letter.toUpperCase())
    : "Pending";
};

const formatDate = (value) => {
  if (!value || value === "0000-00-00") {
    return "Not provided";
  }

  const date = new Date(`${value}T00:00:00`);

  if (Number.isNaN(date.getTime())) {
    return "Not provided";
  }

  return date.toLocaleDateString();
};

export default function AffiliateDashboard() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState("");

  const fetchProfile = useCallback(async () => {
    setLoading(true);

    try {
      const response = await GetService(API_URL.GET_AFFILIATE_PROFILE).then(
        (res) => res?.data?.data,
      );

      console.log("Affiliate profile:", response);

      let promotionMethods = [];

      if (Array.isArray(response?.promotionMethods)) {
        promotionMethods = response.promotionMethods;
      } else if (typeof response?.promotionMethods === "string") {
        try {
          const parsed = JSON.parse(response.promotionMethods);

          promotionMethods = Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          console.error("Unable to parse promotionMethods:", error);
        }
      }

      let socialLinks = {};

      if (response?.socialLinks && typeof response.socialLinks === "object") {
        socialLinks = response.socialLinks;
      } else if (typeof response?.socialLinks === "string") {
        try {
          const parsed = JSON.parse(response.socialLinks);

          socialLinks = parsed && typeof parsed === "object" ? parsed : {};
        } catch (error) {
          console.error("Unable to parse socialLinks:", error);
        }
      }

      setProfile({
        ...response,
        promotionMethods,
        socialLinks,
      });
    } catch (error) {
      console.error(error);

      toastEmmit("error", "Unable to load your influencer profile.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchProfile();
  }, [fetchProfile]);

  const copyValue = async (label, value) => {
    if (!value) {
      toastEmmit("error", `${label} is not available yet.`);
      return;
    }

    try {
      await navigator.clipboard.writeText(value);

      setCopied(label);

      window.setTimeout(() => {
        setCopied("");
      }, 1800);

      toastEmmit("success", `${label} copied`);
    } catch (error) {
      console.error(error);
      toastEmmit("error", `Unable to copy ${label.toLowerCase()}.`);
    }
  };

  if (loading) {
    return (
      <div className="page active text-center py-5" aria-busy="true">
        <span
          className="spinner-border spinner-border-sm me-2"
          role="status"
          aria-hidden="true"
        />
        Loading influencer dashboard...
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="page active">
        <div className="panel-card text-center">
          <div className="panel-title">Influencer dashboard</div>

          <p>We couldn't load your profile right now.</p>

          <button
            type="button"
            className="btn"
            onClick={() => void fetchProfile()}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const totalCommission = Number(profile.total_commission) || 0;

  const paidTotal = Number(profile.total_paid) || 0;

  const explicitPending = profile.pending_payouts ?? profile.total_pending;

  const pendingTotal =
    explicitPending == null
      ? Math.max(totalCommission - paidTotal, 0)
      : Number(explicitPending) || 0;

  const fullName =
    `${profile.firstName ?? ""} ${profile.lastName ?? ""}`.trim();

  const address = [
    profile.streetAddress,
    profile.city,
    profile.state,
    profile.zipCode,
    profile.country,
  ]
    .filter(Boolean)
    .join(", ");

  const status =
    typeof profile?.status === "string"
      ? profile.status.toLowerCase().replace(/[\s-]+/g, "_")
      : "pending";

  const statusMessage =
    status === "rejected"
      ? "Your application was not approved. Review the note from our team below."
      : status === "under_review" || status === "review"
        ? "Your application is being reviewed. We’ll let you know when there’s an update."
        : "Your application has been received and is waiting for review.";

  return (
    <div className="page active affiliate-dashboard">
      {/* Application status */}
      {status !== "approved" && (
        <section
          className={`alert aff-dashboard-status ${
            status === "rejected"
              ? "alert-danger"
              : status === "under_review" || status === "review"
                ? "alert-info"
                : "alert-warning"
          } d-flex gap-3 align-items-center`}
        >
          <CalendarDays size={19} className="flex-shrink-0" />

          <div className="w-100">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <h2 className="h6 mb-0">
                Application {statusText(profile?.status)}
              </h2>

              <span className="badge bg-light text-dark">
                {statusText(profile?.status)}
              </span>
            </div>

            <p className="mb-0">{statusMessage}</p>

            {status === "rejected" && profile.rejectionReason && (
              <p className="mb-0 mt-2 fw-semibold">{profile.rejectionReason}</p>
            )}
          </div>
        </section>
      )}

      {/* Header */}
      <header className="aff-dashboard-header d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3 mb-4">
        <div>
          <div className="aff-dashboard-title-row">
            <span className="aff-dashboard-eyebrow">INFLUENCER CENTER</span>

            <span className={`aff-account-status aff-account-status-${status}`}>
              {statusText(profile.status)}
            </span>
          </div>

          <h2 className="fw-semibold mb-1">Welcome back</h2>

          <p className="text-muted mb-0">
            Your performance, referral tools, and account details.
          </p>
        </div>

        <Link
          to="edit-profile"
          className="btn btn-outline-light aff-dashboard-edit"
        >
          <Pencil size={16} />
          Edit profile
        </Link>
      </header>

      {/* Metrics */}
      <section
        className="row row-cols-2 row-cols-md-3 row-cols-xl-5 g-3 mb-4 aff-dashboard-metrics"
        aria-label="Performance overview"
      >
        {/* Total commission */}
        <div className="col">
          <article className="affiliate-feature-card aff-metric-card aff-metric-commission h-100">
            <div className="aff-metric-top">
              <span className="aff-metric-icon">
                <Wallet size={19} aria-hidden="true" />
              </span>

              <span className="aff-metric-period">LIFETIME</span>
            </div>

            <div className="aff-metric-value">
              {formatMoney(totalCommission)}
            </div>

            <div className="aff-metric-label">Total commission</div>

            <small className="aff-metric-note">Earned to date</small>
          </article>
        </div>

        {/* Paid payouts */}
        <div className="col">
          <article className="affiliate-feature-card aff-metric-card aff-metric-paid h-100">
            <div className="aff-metric-top">
              <span className="aff-metric-icon">
                <Check size={19} aria-hidden="true" />
              </span>

              <span className="aff-metric-period">LIFETIME</span>
            </div>

            <div className="aff-metric-value">{formatMoney(paidTotal)}</div>

            <div className="aff-metric-label">Paid payouts</div>

            <small className="aff-metric-note">Successfully paid</small>
          </article>
        </div>

        {/* Pending payouts */}
        <div className="col">
          <article className="affiliate-feature-card aff-metric-card aff-metric-pending h-100">
            <div className="aff-metric-top">
              <span className="aff-metric-icon">
                <CreditCard size={19} aria-hidden="true" />
              </span>

              <span className="aff-metric-period">CURRENT</span>
            </div>

            <div className="aff-metric-value">{formatMoney(pendingTotal)}</div>

            <div className="aff-metric-label">Pending payouts</div>

            <small className="aff-metric-note">
              {explicitPending == null
                ? "Estimated balance"
                : "Awaiting payout"}
            </small>
          </article>
        </div>

        {/* Total orders */}
        <div className="col">
          <article className="affiliate-feature-card aff-metric-card aff-metric-orders h-100">
            <div className="aff-metric-top">
              <span className="aff-metric-icon">
                <ShoppingBag size={19} aria-hidden="true" />
              </span>

              <span className="aff-metric-period">LIFETIME</span>
            </div>

            <div className="aff-metric-value">{profile.total_orders ?? 0}</div>

            <div className="aff-metric-label">Total orders</div>

            <small className="aff-metric-note">From your referrals</small>
          </article>
        </div>

        {/* Total referrals */}
        <div className="col">
          <article className="affiliate-feature-card aff-metric-card aff-metric-referrals h-100">
            <div className="aff-metric-top">
              <span className="aff-metric-icon">
                <Users size={19} aria-hidden="true" />
              </span>

              <span className="aff-metric-period">LIFETIME</span>
            </div>

            <div className="aff-metric-value">
              {profile.total_referrals ?? 0}
            </div>

            <div className="aff-metric-label">Total referrals</div>

            <small className="aff-metric-note">Customers referred</small>
          </article>
        </div>
      </section>

      {/* Referral details */}
      <section className="affiliate-form-card aff-dashboard-panel mb-4">
        <div className="affiliate-form-header aff-dashboard-panel-header">
          <h4>
            <Link2 size={18} aria-hidden="true" />
            Your referral details
          </h4>

          <p>Share your approved offers with your audience.</p>
        </div>

        <div className="row g-3">
          {/* Referral link */}
          <div className="col-lg-6">
            <div className="payment-option aff-referral-link h-100">
              <span className="text-muted small d-block mb-2">
                Referral link
              </span>

              <div className="d-flex align-items-center justify-content-between gap-2">
                <strong
                  className="text-break"
                  title={profile.affiliateLink || "Not available yet"}
                >
                  {display(profile.affiliateLink)}
                </strong>

                <button
                  type="button"
                  className="btn btn-outline-dark btn-sm aff-copy-button flex-shrink-0"
                  aria-label="Copy referral link"
                  title="Copy referral link"
                  onClick={() =>
                    void copyValue("Referral link", profile.affiliateLink)
                  }
                >
                  {copied === "Referral link" ? (
                    <Check size={16} />
                  ) : (
                    <Clipboard size={16} />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Affiliate code */}
          <div className="col-md-6 col-lg-3">
            <div className="payment-option h-100">
              <span className="text-muted small d-block mb-2">
                Influencer code
              </span>

              <div className="d-flex align-items-center justify-content-between gap-2">
                <strong>{display(profile.affiliateCode)}</strong>

                <button
                  type="button"
                  className="btn btn-outline-dark btn-sm aff-copy-button flex-shrink-0"
                  aria-label="Copy influencer code"
                  title="Copy influencer code"
                  onClick={() =>
                    void copyValue("Influencer code", profile.affiliateCode)
                  }
                >
                  {copied === "Influencer code" ? (
                    <Check size={16} />
                  ) : (
                    <Clipboard size={16} />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Promo code */}
          <div className="col-md-6 col-lg-3">
            <div className="payment-option h-100">
              <span className="text-muted small d-block mb-2">Promo code</span>

              <div className="d-flex align-items-center justify-content-between gap-2">
                <strong>{display(profile.promoCode)}</strong>

                <button
                  type="button"
                  className="btn btn-outline-dark btn-sm aff-copy-button flex-shrink-0"
                  aria-label="Copy promo code"
                  title="Copy promo code"
                  onClick={() =>
                    void copyValue("Promo code", profile.promoCode)
                  }
                >
                  {copied === "Promo code" ? (
                    <Check size={16} />
                  ) : (
                    <Clipboard size={16} />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Information sections */}
      <div className="row g-4">
        {/* Personal information */}
        <div className="col-xl-6">
          <section className="affiliate-form-card aff-dashboard-panel h-100">
            <div className="affiliate-form-header aff-dashboard-panel-header">
              <h4>
                <UserRound size={18} aria-hidden="true" />
                Personal information
              </h4>

              <p>Your registered contact and location details.</p>
            </div>

            <div className="d-flex align-items-center mb-3">
              <img
                src={getImageSource(profile.image_url)}
                alt={fullName || "Profile"}
                className="rounded-circle me-3"
                width={64}
                height={64}
              />

              <div>
                <strong className="d-block">{display(fullName)}</strong>

                <span className="text-muted">
                  {display(profile.workingProfession)}
                </span>
              </div>
            </div>

            <div className="row g-4">
              <div className="col-md-6">
                <span className="text-muted small d-block">Email</span>

                <strong>{display(profile.email)}</strong>
              </div>

              <div className="col-md-6">
                <span className="text-muted small d-block">Phone</span>

                <strong>{display(profile.phone)}</strong>
              </div>

              <div className="col-md-6">
                <span className="text-muted small d-block">Date of birth</span>

                <strong>{formatDate(profile.dateOfBirth)}</strong>
              </div>

              <div className="col-md-6">
                <span className="text-muted small d-block">Address</span>

                <strong>{address || "Not provided"}</strong>
              </div>
            </div>
          </section>
        </div>

        {/* Business information */}
        <div className="col-xl-6">
          <section className="affiliate-form-card aff-dashboard-panel h-100">
            <div className="affiliate-form-header aff-dashboard-panel-header">
              <h4>
                <Building2 size={18} aria-hidden="true" />
                Business information
              </h4>

              <p>Details submitted with your application.</p>
            </div>

            <div className="row g-4">
              <div className="col-md-6">
                <span className="text-muted small d-block">Account type</span>

                <strong>{display(profile.affiliateType)}</strong>
              </div>

              <div className="col-md-6">
                <span className="text-muted small d-block">Business name</span>

                <strong>{display(profile.businessName)}</strong>
              </div>

              <div className="col-md-6">
                <span className="text-muted small d-block">Profession</span>

                <strong>{display(profile.workingProfession)}</strong>
              </div>

              <div className="col-md-6">
                <span className="text-muted small d-block">Website</span>

                <strong>
                  {profile.website ? (
                    <a href={profile.website} target="_blank" rel="noreferrer">
                      Visit website <ExternalLink size={13} />
                    </a>
                  ) : (
                    "Not provided"
                  )}
                </strong>
              </div>

              <div className="col-md-6">
                <span className="text-muted small d-block">
                  {profile.affiliateType === "Business" ? "EIN" : "Tax ID"}
                </span>

                <strong>
                  {maskValue(
                    profile.affiliateType === "Business"
                      ? profile.einNumber
                      : profile.socialSecurityNumber,
                  )}
                </strong>
              </div>

              <div className="col-md-6">
                <span className="text-muted small d-block">Location</span>

                <strong>{address || "Not provided"}</strong>
              </div>
            </div>
          </section>
        </div>

        {/* Promotional information */}
        <div className="col-xl-6">
          <section className="affiliate-form-card aff-dashboard-panel h-100">
            <div className="affiliate-form-header aff-dashboard-panel-header">
              <h4>
                <Megaphone size={18} aria-hidden="true" />
                Promotional information
              </h4>

              <p>Channels and audience you plan to reach.</p>
            </div>

            <div className="mb-3">
              {(profile.promotionMethods ?? []).length > 0 ? (
                profile.promotionMethods.map((method) => (
                  <span
                    className="badge bg-light text-dark border text-capitalize me-2 mb-2"
                    key={method}
                  >
                    {method}
                  </span>
                ))
              ) : (
                <p className="text-muted mb-0">
                  No promotion channels provided.
                </p>
              )}
            </div>

            <div className="row g-4">
              <div className="col-md-6">
                <span className="text-muted small d-block">Audience size</span>

                <strong>{display(profile.audienceSize)}</strong>
              </div>

              <div className="col-12">
                <span className="text-muted small d-block">
                  Why iPhotoCanvas
                </span>

                <strong>{display(profile.reason)}</strong>
              </div>
            </div>
          </section>
        </div>

        {/* Payment information */}
        <div className="col-xl-6">
          <section className="affiliate-form-card aff-dashboard-panel h-100">
            <div className="affiliate-form-header aff-dashboard-panel-header">
              <h4>
                <Landmark size={18} aria-hidden="true" />
                Payment information
              </h4>

              <p>Verified payout details on file.</p>
            </div>

            <div className="row g-4">
              <div className="col-md-6">
                <span className="text-muted small d-block">Payment method</span>

                <strong className="text-capitalize">
                  {display(profile.paymentMethod?.replaceAll("_", " "))}
                </strong>
              </div>

              {profile.paymentMethod === "paypal" ? (
                <div className="col-md-6">
                  <span className="text-muted small d-block">PayPal email</span>

                  <strong>{display(profile.paypalEmail)}</strong>
                </div>
              ) : (
                <>
                  <div className="col-md-6">
                    <span className="text-muted small d-block">
                      Account holder
                    </span>

                    <strong>{display(profile.accountHolderName)}</strong>
                  </div>

                  <div className="col-md-6">
                    <span className="text-muted small d-block">Bank name</span>

                    <strong>{display(profile.bankName)}</strong>
                  </div>

                  <div className="col-md-6">
                    <span className="text-muted small d-block">
                      Account number
                    </span>

                    <strong>{maskValue(profile.accountNumber)}</strong>
                  </div>

                  <div className="col-md-6">
                    <span className="text-muted small d-block">
                      Routing number
                    </span>

                    <strong>{maskValue(profile.routingNumber)}</strong>
                  </div>

                  <div className="col-md-6">
                    <span className="text-muted small d-block">
                      SWIFT / IBAN
                    </span>

                    <strong>
                      {maskValue(profile.swiftCode || profile.iban)}
                    </strong>
                  </div>
                </>
              )}
            </div>

            <div className="alert alert-secondary mt-3 mb-0">
              Payment details are protected and can’t be changed here.
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
